/**
 * **Two occurrences, side by side, in the pane.** KANON `ankiety-klienta-2026-09-16`,
 * `szkice/kierunek-a-porownanie.html`.
 *
 * ── The CHANGE column comes second, right after the question ─────────────────────────────────
 * Owner, 16 September 2026: „żeby szybciej się czytało". The sketch puts it last, which is the
 * spreadsheet's order — read the two values, subtract, then read the answer. A trainer scanning
 * twelve rows for what moved is looking for the answer, and it is now the first thing under their
 * eye. The two readings stay, because a change without its ends is a number nobody can check.
 *
 * ── A real `<table>` ─────────────────────────────────────────────────────────────────────────
 * Question down the side, two occurrences across: every cell needs both headers to mean anything,
 * and a table gives that without an ARIA claim.
 *
 * ── „Zamień strony" is one state, not a second projection ────────────────────────────────────
 * The press swaps the PAIR the tab holds, so this is handed the two ids the other way round and
 * projects them exactly as it projects any other pair. There is no second code path and no second
 * set of rules for which side is the baseline — which is what stops the change column and the
 * columns it is computed from ever disagreeing about direction.
 *
 * ── The HEAD is `SurveyPaneHead`, outside the box this is drawn in ───────────────────────────
 * Including „zamień strony" itself. The content here is keyed on the pair and rebuilt by Motion on
 * every press, so a control drawn inside it lost focus the moment it was used.
 *
 * ── The THEAD sticks ─────────────────────────────────────────────────────────────────────────
 * ZMIANA | tydz. A | tydz. B stay at the top of the tab's own scroller while the measurement rows
 * pass under them: a change column read twelve rows down says nothing without the two dates it is
 * between. See `SurveyPane.module.css`.
 */

import { useTranslation } from 'react-i18next';

import hidden from '~/shared/ui/visuallyHidden.module.css';

import { useReadingLabels, SurveyReading, TONE_CLASS, ChangeArrowIcon } from '~/features/survey-report';
import { buildSurveyCompare } from '../model/surveyCompare';
import type { SurveyHistoryView } from '~/features/survey-report';
import styles from './SurveyPane.module.css';

export interface SurveyCompareProps {
  readonly view: SurveyHistoryView;
  /** The two occurrences, in the order they are drawn. */
  readonly leftId: number;
  readonly rightId: number;
  /**
   * „Porównanie · tydz. 6 → tydz. 8" — the head's own title, reused as this table's name.
   *
   * Handed in rather than built again: the chart strip above publishes a hidden data table per
   * card, so a screen reader asked for "the tables on this page" is given four, and the one the
   * trainer is reading has to say which comparison it is. Two constructions of that sentence would
   * have been the head's and this one's.
   */
  readonly caption: string;
}

export function SurveyCompare({ view, leftId, rightId, caption }: SurveyCompareProps) {
  const { t, i18n } = useTranslation();
  const labels = useReadingLabels();
  const compare = buildSurveyCompare(view, leftId, rightId, { t, locale: i18n.language });
  if (compare === null) return null;

  return (
    <table className={styles.compareTable}>
      <caption className={hidden.srOnly}>{caption}</caption>
      <thead>
        <tr>
          <td className={styles.compareHeadCell} />
          <th scope="col" className={styles.compareChangeHead}>
            {t('web:clientDetail.surveys.changeHead')}
          </th>
          {[compare.left, compare.right].map((column) => (
            <th scope="col" className={styles.compareHeadCell} key={column.id}>
              <span className={styles.compareHeadOrdinal}>{column.ordinalLabel}</span>
              <span className={styles.compareHeadWhen}>
                {t('web:clientDetail.surveys.compareColumnWhen', {
                  date: column.dateLabel,
                  status: column.statusLabel,
                })}
              </span>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {compare.rows.map((row) => (
          <tr key={row.key}>
            <th scope="row" className={styles.compareLabel}>
              <span
                className={`${String(styles.compareName)} ${row.sub ? String(styles.compareSub) : ''}`}
              >
                {row.label}
              </span>
              {row.kind === 'measurementGroup' && compare.measurementSummary !== null ? (
                <em className={styles.compareHeadWhen}>{compare.measurementSummary}</em>
              ) : null}
            </th>
            <td className={`${String(styles.compareChange)} ${TONE_CLASS[row.changeTone ?? 'flat']}`}>
              {/* Three different statements, and they are not interchangeable. A figure is a
                * change. „brak" is "this row COULD have carried one and one side never answered".
                * And a blank cell is "there is no arithmetic between two photographs" — printing
                * „brak" there claimed a missing measurement that was never asked for. */}
              {row.comparable ? (
                row.changeText === null ? (
                  t('web:clientDetail.surveys.noChange')
                ) : (
                  <>
                    <ChangeArrowIcon sign={Math.sign(row.change ?? 0)} />
                    {row.changeText}
                  </>
                )
              ) : null}
            </td>
            {[row.left, row.right].map((cell) => (
              <td className={styles.compareCell} key={cell.columnId}>
                <SurveyReading
                  cell={cell}
                  kind={row.kind}
                  showDelta={false}
                  showScaleFigure
                  labels={labels}
                  mediaClassName={
                    row.kind === 'photos'
                      ? String(styles.comparePhotos)
                      : String(styles.compareClip)
                  }
                  playable
                />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
